import {
  BadgeCheckIcon,
  CheckIcon,
  ClockIcon,
  DocumentSearchIcon,
  InformationCircleIcon,
  ReceiptRefundIcon,
  RefreshIcon,
  XCircleIcon,
} from '@heroicons/react/outline';
import type {SVGProps} from 'react';

export {AfriexTransactionType} from '@afriex/core/domain/enums';
import {
  AfriexLedgerReasons,
  AfriexTransactionStatus,
} from '@afriex/core/domain/enums';

// `LedgerReasons` is the historical admin-local name for core's
// `AfriexLedgerReasons`. Aliased rather than renamed so existing call sites and
// the `src/types` barrel keep resolving.
export {AfriexLedgerReasons as LedgerReasons};

export enum FeeTransactionTypes {
  DEPOSIT = 'DEPOSIT',
  WITHDRAW = 'WITHDRAW',
  SWAP = 'SWAP',
  E2E = 'E2E',
  VIRTUAL_CARD_LOAD = 'VIRTUAL_CARD_LOAD',
  VIRTUAL_CARD_UNLOAD = 'VIRTUAL_CARD_UNLOAD',
  VIRTUAL_CARD_SPEND = 'VIRTUAL_CARD_SPEND',
  ACH = 'ACH',
}

export {AfriexTransactionChannel} from '@afriex/core/domain/enums';

export enum FeeTransactionChannels {
  BANK = 'BANK',
  WALLET = 'WALLET',
  RFP = 'RFP',
  INTERAC = 'INTERAC',
  VIRTUAL_CARD = 'VIRTUAL_CARD',
  SWIFT = 'SWIFT',
  VIRTUAL_BANK_ACCOUNT = 'VIRTUAL_BANK_ACCOUNT',
  ACH_BANK_ACCOUNT = 'ACH_BANK_ACCOUNT',
  REGULATED_CARD = 'REGULATED_CARD',
  UNREGULATED_CARD = 'UNREGULATED_CARD',
}

// Values are string literals on purpose — they must NOT reference the imported
// `AfriexTransactionType` members. esbuild (Vite/Vitest) transpiles each file in
// isolation and cannot prove a cross-package enum member is a string, so it
// treats members initialized from an imported enum like a numeric enum and emits
// reverse mappings (e.g. `WITHDRAW -> ADMIN_WITHDRAW`). That bidirectional map
// breaks `getTxTypeCheck`, which then mis-normalizes every plain type to its
// `ADMIN_` variant (surfacing as source-instead-of-destination currency in the
// transaction tables). The literals below equal the core enum values
// (BONUS = 'WELCOME_BONUS') so there is no semantic change.
export enum AdminTransactionTypes {
  ADMIN_DEPOSIT = 'DEPOSIT',
  ADMIN_WITHDRAW = 'WITHDRAW',
  ADMIN_WELCOME_BONUS = 'WELCOME_BONUS',
  ADMIN_REFERRAL = 'REFERRAL',
}

export {AfriexTransactionStatus} from '@afriex/core/domain/enums';

export interface IBankAccountInfo {
  account_number: string;
  bank_name: string;
  account_name: string;
  phoneNumber: string;
  bank_code: string;
}

export interface AfriexAgentDetails {
  userName: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  country: string;
}

export interface LedgerRequestParams {
  currency: string;
  amount: number;
  userId: string;
  action: 'credit' | 'debit';
  reason: AfriexLedgerReasons;
}

export const statusIcons: Partial<{
  [key in AfriexTransactionStatus]: React.FC<SVGProps<SVGSVGElement>>;
}> = {
  [AfriexTransactionStatus.PENDING]: ClockIcon,
  [AfriexTransactionStatus.CANCELLED]: XCircleIcon,
  [AfriexTransactionStatus.SUCCESS]: CheckIcon,
  [AfriexTransactionStatus.PROCESSING]: RefreshIcon,
  [AfriexTransactionStatus.FAILED]: XCircleIcon,
  [AfriexTransactionStatus.REJECTED]: XCircleIcon,
  [AfriexTransactionStatus.REFUNDED]: ReceiptRefundIcon,
  [AfriexTransactionStatus.IN_REVIEW]: DocumentSearchIcon,
  [AfriexTransactionStatus.RETRY]: RefreshIcon,
  [AfriexTransactionStatus.CUSTOMER_ACTION_REQUIRED]: RefreshIcon,
  [AfriexTransactionStatus.ENGINEERING_REVIEW_REQUIRED]: RefreshIcon,
  [AfriexTransactionStatus.RFI_REQUESTED]: DocumentSearchIcon,
  [AfriexTransactionStatus.DISPUTED]: DocumentSearchIcon,
  [AfriexTransactionStatus.DISPUTE_RESOLVED]: BadgeCheckIcon,
  [AfriexTransactionStatus.DISPUTE_WON]: BadgeCheckIcon,
  [AfriexTransactionStatus.DISPUTE_LOST]: XCircleIcon,
  [AfriexTransactionStatus.DISPUTE_EVIDENCE_SUBMITTED]: DocumentSearchIcon,
  [AfriexTransactionStatus.UNKNOWN]: InformationCircleIcon,
};

// These are custom colors defined in tailwind.config.js
export const statusColors: {[key: string | AfriexTransactionStatus]: string} = {
  pending: 'pending',
  successful: 'successful',
  failed: 'failed',
  cancelled: 'failed',
  completed: 'successful',
  rejected: 'failed',
  in_review: 'refunded',
  unknown: 'gray',
  disputed: 'indigo',
  dispute_resolved: 'successful',
  dispute_won: 'successful',
  dispute_lost: 'failed',
  customer_action_required: 'indigo',
  engineering_review_required: 'refunded',
  dispute_evidence_submitted: 'blue',
  [AfriexTransactionStatus.PENDING]: 'pending',
  [AfriexTransactionStatus.SUCCESS]: 'successful',
  [AfriexTransactionStatus.FAILED]: 'failed',
  [AfriexTransactionStatus.REJECTED]: 'failed',
  [AfriexTransactionStatus.CANCELLED]: 'failed',
  [AfriexTransactionStatus.REFUNDED]: 'refunded',
  [AfriexTransactionStatus.RETRY]: 'refunded',
  [AfriexTransactionStatus.IN_REVIEW]: 'refunded',
  [AfriexTransactionStatus.ENGINEERING_REVIEW_REQUIRED]: 'refunded',
  [AfriexTransactionStatus.RFI_REQUESTED]: 'refunded',
  [AfriexTransactionStatus.DISPUTED]: 'indigo',
  [AfriexTransactionStatus.CUSTOMER_ACTION_REQUIRED]: 'indigo',
  [AfriexTransactionStatus.DISPUTE_RESOLVED]: 'successful',
  [AfriexTransactionStatus.DISPUTE_WON]: 'successful',
  [AfriexTransactionStatus.DISPUTE_LOST]: 'failed',
  [AfriexTransactionStatus.DISPUTE_EVIDENCE_SUBMITTED]: 'blue',
  [AfriexTransactionStatus.UNKNOWN]: 'gray',
};
